<template>
  <VaSidebar
    v-model="writableVisible"
    class="app-sidebar"
    :class="{
      'app-sidebar--overlay': mobile || tablet,
      'app-sidebar--minimized': isIconRail,
    }"
    :width="sidebarWidth"
    :color="color"
    :minimized="isIconRail"
    :minimized-width="railWidth"
    :animated="animated"
    :key="`sidebar-${adminStore.role}-${isAdmin}`"
  >
    <div class="app-sidebar__inner">
      <!-- <RouterLink
        v-if="isIconRail"
        to="/"
        class="app-sidebar__brand"
        :aria-label="brandTitle || 'Home'"
      >
        <img :src="brandLogo" class="app-sidebar__brand-logo" :alt="brandTitle || 'Logo'" />
      </RouterLink> -->

      <VaAccordion v-model="value" multiple class="app-sidebar__accordion">
        <VaCollapse
          v-for="(route, index) in filteredRoutes"
          :key="`${route.name}-${currentRole}`"
          class="app-sidebar__collapse"
        >
          <template #expand-icon>
            <span class="app-sidebar__expand-slot" aria-hidden="true" />
          </template>
          <template #header="{ value: isCollapsed }">
            <div
              class="app-sidebar__rail-anchor"
              @mouseenter="isIconRail && openRailFlyout(route, index, $event)"
              @mouseleave="isIconRail && scheduleCloseRailFlyout()"
            >
              <VaSidebarItem
                class="app-sidebar__item"
                :to="rootItemTo(route)"
                :active="routeHasActiveChild(route)"
                :active-color="activeColor"
                :text-color="textColor(route)"
                :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(getDisplayName(route))}`"
                :title="isIconRail ? undefined : t(getDisplayName(route))"
                role="button"
                hover-opacity="0.10"
                @click="onRootItemClick(route, index)"
              >
                <VaSidebarItemContent class="app-sidebar__item-content app-sidebar__item-content--root">
                  <VaIcon
                    v-if="route.meta.icon"
                    aria-hidden="true"
                    :name="route.meta.icon"
                    :size="isIconRail ? '20px' : '18px'"
                    :color="iconColor(route)"
                  />
                  <VaSidebarItemTitle class="app-sidebar__title">
                    <span>{{ t(getDisplayName(route)) }}</span>
                    <VaIcon
                      v-if="route.children"
                      class="app-sidebar__chevron"
                      :class="{ 'is-open': isCollapsed }"
                      name="expand_more"
                      size="18px"
                    />
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </template>

          <!-- Dropdown -->
          <template v-if="route.children?.length" #body>
            <div class="app-sidebar__children">
              <div
                v-for="(childRoute, index2) in filteredChildren(route.children)"
                :key="index2"
                class="app-sidebar__child-block"
              >
                <!-- Level 3 -->
                <div v-if="childRoute.children && childRoute.children.length > 0">
                  <div>
                    <VaSidebarItem
                      class="app-sidebar__item app-sidebar__item--child"
                      :to="undefined"
                      :active="routeHasActiveGrandChild(childRoute)"
                      :active-color="activeColor"
                      :text-color="textColor(childRoute)"
                      :aria-label="`Open category ${t(getDisplayName(childRoute))}`"
                      role="button"
                      hover-opacity="0.10"
                      @click="toggleChildCollapse(index2)"
                    >
                      <VaSidebarItemContent class="app-sidebar__item-content app-sidebar__item-content--child">
                        <VaIcon
                          v-if="childRoute.meta.icon"
                          :name="childRoute.meta.icon"
                          size="17px"
                          :color="iconColor(childRoute)"
                        />
                        <VaSidebarItemTitle class="app-sidebar__title">
                          <span>{{ t(getDisplayName(childRoute)) }}</span>
                          <VaIcon
                            class="app-sidebar__chevron"
                            :class="{ 'is-open': isChildExpanded(index2) }"
                            name="expand_more"
                            size="16px"
                          />
                        </VaSidebarItemTitle>
                      </VaSidebarItemContent>
                    </VaSidebarItem>

                    <div v-show="isChildExpanded(index2)" class="level-3-children">
                      <div
                        v-for="(grandChildRoute, index3) in filteredChildren(childRoute.children)"
                        :key="index3"
                      >
                        <VaSidebarItem
                          class="app-sidebar__item app-sidebar__item--grand"
                          :to="{ name: getRouteName(grandChildRoute.name) }"
                          :active="isActiveChildRoute(grandChildRoute)"
                          :active-color="activeColor"
                          :text-color="textColor(grandChildRoute)"
                          :aria-label="`Visit ${t(getDisplayName(grandChildRoute))}`"
                          hover-opacity="0.10"
                        >
                          <VaSidebarItemContent class="app-sidebar__item-content app-sidebar__item-content--grand">
                            <VaIcon
                              v-if="grandChildRoute.meta.icon"
                              :name="grandChildRoute.meta.icon"
                              size="16px"
                              :color="iconColor(grandChildRoute)"
                            />
                            <VaSidebarItemTitle class="app-sidebar__title app-sidebar__title--sm">
                              {{ t(getDisplayName(grandChildRoute)) }}
                            </VaSidebarItemTitle>
                          </VaSidebarItemContent>
                        </VaSidebarItem>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Regular child -->
                <div v-else>
                  <VaSidebarItem
                    class="app-sidebar__item app-sidebar__item--child"
                    :to="{ name: getRouteName(childRoute.name) }"
                    :active="isActiveChildRoute(childRoute)"
                    :active-color="activeColor"
                    :text-color="textColor(childRoute)"
                    :aria-label="`Visit ${t(getDisplayName(childRoute))}`"
                    hover-opacity="0.10"
                  >
                    <VaSidebarItemContent class="app-sidebar__item-content app-sidebar__item-content--child">
                      <VaIcon
                        v-if="childRoute.meta.icon"
                        :name="childRoute.meta.icon"
                        size="17px"
                        :color="iconColor(childRoute)"
                      />
                      <VaSidebarItemTitle class="app-sidebar__title">
                        {{ t(getDisplayName(childRoute)) }}
                      </VaSidebarItemTitle>
                    </VaSidebarItemContent>
                  </VaSidebarItem>
                </div>
              </div>
            </div>
          </template>
        </VaCollapse>
      </VaAccordion>
    </div>

    <Teleport to="body">
      <div
        v-if="isIconRail && railFlyout"
        class="app-sidebar-flyout"
        :style="railFlyoutStyle"
        @mouseenter="cancelCloseRailFlyout"
        @mouseleave="scheduleCloseRailFlyout"
      >
        <RouterLink
          v-if="!railFlyoutChildren.length"
          :to="{ name: getRouteName(railFlyout.route.name) }"
          class="app-sidebar-flyout__header app-sidebar-flyout__header--link"
          @click="closeRailFlyout"
        >
          <VaIcon
            v-if="railFlyout.route.meta?.icon"
            :name="railFlyout.route.meta.icon"
            size="16px"
            color="primary"
          />
          <span class="app-sidebar-flyout__title">{{ t(getDisplayName(railFlyout.route)) }}</span>
        </RouterLink>
        <div v-else class="app-sidebar-flyout__header">
          <VaIcon
            v-if="railFlyout.route.meta?.icon"
            :name="railFlyout.route.meta.icon"
            size="16px"
            color="primary"
          />
          <span class="app-sidebar-flyout__title">{{ t(getDisplayName(railFlyout.route)) }}</span>
        </div>

        <div v-if="railFlyoutChildren.length" class="app-sidebar-flyout__list">
          <template v-for="(childRoute, childIndex) in railFlyoutChildren" :key="`${childRoute.name}-${childIndex}`">
            <div v-if="childRoute.children?.length" class="app-sidebar-flyout__group">
              <div class="app-sidebar-flyout__group-label">
                <VaIcon
                  v-if="childRoute.meta?.icon"
                  :name="childRoute.meta.icon"
                  size="14px"
                />
                <span>{{ t(getDisplayName(childRoute)) }}</span>
              </div>
              <RouterLink
                v-for="(grandChildRoute, grandIndex) in filteredChildren(childRoute.children)"
                :key="`${grandChildRoute.name}-${grandIndex}`"
                :to="{ name: getRouteName(grandChildRoute.name) }"
                class="app-sidebar-flyout__link"
                :class="{ 'is-active': isActiveChildRoute(grandChildRoute) }"
                @click="closeRailFlyout"
              >
                <VaIcon
                  v-if="grandChildRoute.meta?.icon"
                  :name="grandChildRoute.meta.icon"
                  size="14px"
                />
                <span>{{ t(getDisplayName(grandChildRoute)) }}</span>
              </RouterLink>
            </div>

            <RouterLink
              v-else
              :to="{ name: getRouteName(childRoute.name) }"
              class="app-sidebar-flyout__link"
              :class="{ 'is-active': isActiveChildRoute(childRoute) }"
              @click="closeRailFlyout"
            >
              <VaIcon
                v-if="childRoute.meta?.icon"
                :name="childRoute.meta.icon"
                size="14px"
              />
              <span>{{ t(getDisplayName(childRoute)) }}</span>
            </RouterLink>
          </template>
        </div>
      </div>
    </Teleport>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, onMounted, onBeforeUnmount, type CSSProperties } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useAdminStore } from '../../stores/admin'
import { useSidebarPermissions } from './SideBarPermission'
import { getCurrentRole } from '../../services/authContext'
import { logo as brandLogo, title as brandTitle } from '../../const'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    tablet: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
    animated: { type: Boolean, default: true },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const adminStore = useAdminStore()
    
    // Utilisation du composable de permissions
    const {
      currentRole,
      isSupport,
      filterRoutes,
      filterChildren,
      getRouteName,
      getDisplayName
    } = useSidebarPermissions()

    const value = ref<boolean[]>([false])
    const platform = import.meta.env.VITE_APP_PLATFORM || 'local'
    const expandedRoutes = ref<Set<string>>(new Set())
    const expandedChildren = ref<Set<number>>(new Set())
    const manuallyExpandedRoutes = ref<Set<number>>(new Set())

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) return route.name === section.name
      return section.children.some((child) => {
        if (child.name === route.name) return true
        return !!child.children?.some(({ name }) => name === route.name)
      })
    }

    const routeHasActiveGrandChild = (section: INavigationRoute) => {
      if (!section.children) return false
      return section.children.some((child) => {
        if (!child.children) return false
        return child.children.some(({ name }) => route.name === name)
      })
    }

    const toggleChildCollapse = (index: number) => {
      if (expandedChildren.value.has(index)) {
        expandedChildren.value.delete(index)
      } else {
        expandedChildren.value.add(index)
      }
    }

    const isChildExpanded = (index: number) => expandedChildren.value.has(index)

    const getChildArrowDirection = (index: number) => 
      isChildExpanded(index) ? 'va-arrow-up' : 'va-arrow-down'

    const toggleManualExpand = (index: number) => {
      if (manuallyExpandedRoutes.value.has(index)) {
        manuallyExpandedRoutes.value.delete(index)
      } else {
        manuallyExpandedRoutes.value.add(index)
      }
    }

    const isIconRail = computed(() => props.minimized && !props.mobile && !props.tablet)
    const railWidth = '4.5rem'

    type RailFlyoutState = {
      route: INavigationRoute
      index: number
      top: number
      left: number
    }

    const railFlyout = ref<RailFlyoutState | null>(null)
    let railFlyoutCloseTimer: ReturnType<typeof setTimeout> | null = null

    const railFlyoutChildren = computed(() => {
      if (!railFlyout.value?.route.children?.length) return []
      return filteredChildren(railFlyout.value.route.children)
    })

    const railFlyoutStyle = computed((): CSSProperties => {
      if (!railFlyout.value) return {}
      return {
        top: `${railFlyout.value.top}px`,
        left: `${railFlyout.value.left}px`,
      }
    })

    const cancelCloseRailFlyout = () => {
      if (railFlyoutCloseTimer) {
        clearTimeout(railFlyoutCloseTimer)
        railFlyoutCloseTimer = null
      }
    }

    const closeRailFlyout = () => {
      cancelCloseRailFlyout()
      railFlyout.value = null
    }

    const scheduleCloseRailFlyout = () => {
      cancelCloseRailFlyout()
      railFlyoutCloseTimer = setTimeout(() => {
        railFlyout.value = null
        railFlyoutCloseTimer = null
      }, 140)
    }

    const openRailFlyout = (section: INavigationRoute, index: number, event: MouseEvent) => {
      cancelCloseRailFlyout()
      const target = event.currentTarget as HTMLElement | null
      if (!target) return

      const rect = target.getBoundingClientRect()
      const gap = 8
      const estimatedHeight = 48 + (section.children?.length ? Math.min(section.children.length, 8) * 36 : 0)
      const maxTop = Math.max(8, window.innerHeight - estimatedHeight - 8)
      const top = Math.min(Math.max(8, rect.top), maxTop)

      railFlyout.value = {
        route: section,
        index,
        top,
        left: rect.right + gap,
      }
    }

    const getFirstNavigableName = (section: INavigationRoute): string | undefined => {
      const children = filteredChildren(section.children)
      if (!children.length) {
        return section.children ? undefined : getRouteName(section.name)
      }

      for (const child of children) {
        if (child.children?.length) {
          const grandChildren = filteredChildren(child.children)
          if (grandChildren[0]) {
            return getRouteName(grandChildren[0].name)
          }
        } else {
          return getRouteName(child.name)
        }
      }

      return undefined
    }

    const rootItemTo = (section: INavigationRoute) => {
      if (section.children?.length) {
        if (isIconRail.value) {
          const name = getFirstNavigableName(section)
          return name ? { name } : undefined
        }
        return undefined
      }
      return { name: getRouteName(section.name) }
    }

    const onRootItemClick = (section: INavigationRoute, index: number) => {
      // In icon-rail mode, navigation is handled by `:to` / router-link
      if (isIconRail.value) return

      if (section.children) {
        toggleManualExpand(index)
      }
    }

    const setActiveExpand = () => {
      try {
        const routes = filteredRoutes.value
        if (routes.length === 0) return

        const expandedArr = routes.map((section: INavigationRoute, index: number) => {
          // Leaf routes (e.g. Users) must never auto-expand — empty body caused a ghost shadow line
          if (!section.children?.length) {
            return manuallyExpandedRoutes.value.has(index)
          }

          const hasActiveChild = section.children.some(({ name }) => route.name === name)
          const hasActiveGrandChild = routeHasActiveGrandChild(section)
          const isManuallyExpanded = manuallyExpandedRoutes.value.has(index)

          if (!isAdmin.value) {
            return hasActiveChild || hasActiveGrandChild || isManuallyExpanded
          }
          return hasActiveChild || hasActiveGrandChild
        })

        value.value = expandedArr.map((shouldExpand, index) => {
          if (manuallyExpandedRoutes.value.has(index)) return true
          return shouldExpand
        })

        filteredRoutes.value.forEach((route) => {
          if (route.children) {
            route.children.forEach((child, index) => {
              if (child.children && routeHasActiveGrandChild(child)) {
                expandedChildren.value.add(index)
              }
            })
          }
        })
      } catch (e) {
        // Silently catch errors
      }
    }

    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      if (props.tablet) return '70vw'
      return '280px'
    })
    // Always opaque — translucent color was bleeding page content on mobile overlay
    const color = computed(() => 'backgroundPrimary')
    const activeColor = computed(() => colorToRgba(getColor('primary'), 0.12))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const isAdmin = computed(() => {
      const role = getCurrentRole()
      return role === 'admin' || role === 'super_admin'
    })

    // Filtrer les routes et sections selon le rôle
    const filteredRoutes = computed(() => {
      // Start from full routes list
      let routes = navigationRoutes.routes

      // If NOT CN variant (yd, tk, local), remove top-level users_tab route entirely
      // if (platform !== 'cn' && platform !== 'tk') {
      //   routes = routes.filter((r: INavigationRoute) => r.name !== 'users_tab')
      // }

      if (isAdmin.value) {
        return routes
      }

      // Pour les agents, filtrer certaines routes / sections complètement admin-only
      return routes.filter((route) => {
        const hiddenForAgents = [
          // 'dashboard',
          'announcements',
          'vip-rights',           
          'landing-management',   
          'domain',               
          'versions-section',     
          'videos',               
          'posts',
          'users_tab',                
          'country',  
          'steal_stat',  
          'chat-section',
          'chat-conversations',   
          'content-section',     
          'config',               
          'translate',            
          'game-section',         
          'exchange', 
          'vip-rights',
          'landing-management',
          'domain',
          'versions-section',
          // 'videos',
          'price-config',
          // 'posts',
          'country',
          'config',
          'translate',
          'game-section',
          'exchange',
          'videos',
          'posts',
          'orders',
          'landing',
          'steal_stat',
          'wallet-management',
          'system-settings',
          'admin-agent-withdraw',
          'ads'

         
        ]
        return !hiddenForAgents.includes(route.name)
      })
    })

    // Filtrer les routes enfants selon le rôle
    const filteredChildren = (children?: INavigationRoute[]) => {
      if (!children) return []

      const result = children.filter((child) => {
        // Hide users page entirely when NOT running CN variant (yd) - but we want it visible for yd
        // No condition here - let it be controlled by hiddenForAgents array

        // Routes hidden from agents
        const hiddenForAgents = [
          'announcements',
          'annonce-editor_dark',
          //'annonce-editor',
          // 'promotion-link',
          'landing-management',
          'promotion-platform',
          'promotions',
          // Admin-only VIP pages
          'vip-rights', // VIP Rights (hide for agents)
          'agents-vip', // Agents VIP (hide for agents)
          'vip', // VIP Plans (hide for agents)
          // Admin-only pages that should not be visible to agents
          'users',
          'price-config',
          'versions',
          'versions-link',
          'videos',
          'category',
          'chat-log',
          // 'chat-conversations',
          'tag',
          // 'users_tab',     
          'post',
          'category_p',
          'tag_p',
          'emoji',
          'banner',
          'ads',
          'mangas',
          // Also hide these top-level admin sections if they ever appear as children
          'country',
          'config',
          'translate',
          'game-section',
          'exchange',
          'users',
          'price-config',
          'videos',
          'post',
          'users-vip',
          'landing-page',
          'landing',
          'admin-agent-withdraw',
        ]

        // Hide vip-setting for admins only (show for agents)
        if (isAdmin.value && child.name === 'vip-setting') {
          return false
        }

        // Hide promotion-link for admins only (show for agents)
        if (isAdmin.value && child.name === 'promotion-link') {
          return false
        }

        if (!isAdmin.value && hiddenForAgents.includes(child.name)) {
          return false
        }

        // If platform is cn, hide the users page for everyone
        // if (platform === 'cn' && child.name === 'users') return false

        return true
      })

      return result
    }

    watch(
      () => route.fullPath,
      () => {
        if (isIconRail.value) {
          closeRailFlyout()
          return
        }
        nextTick(() => {
          setActiveExpand()
        })
      },
    )

    watch(
      isIconRail,
      (rail) => {
        nextTick(() => {
          if (rail) {
            value.value = filteredRoutes.value.map(() => false)
            manuallyExpandedRoutes.value.clear()
          } else {
            closeRailFlyout()
            setActiveExpand()
          }
        })
      },
      { immediate: true },
    )

    // Watch pour réagir aux changements de rôle (connexion directe agent)
    watch(
      () => adminStore.role,
      () => {
        nextTick(() => {
          setActiveExpand()
        })
      },
      { immediate: true },
    )

    // Initialize on mount — getAdminAuthenticate partagé (cache store, dédoublonné avec la navbar)
    onMounted(() => {
      adminStore.getAdminAuthenticate().then(() => {
        nextTick(() => setActiveExpand())
      })
    })

    onBeforeUnmount(() => {
      closeRailFlyout()
    })

    return {
      writableVisible,
      sidebarWidth,
      railWidth,
      isIconRail,
      brandLogo,
      brandTitle,
      railFlyout,
      railFlyoutChildren,
      railFlyoutStyle,
      openRailFlyout,
      scheduleCloseRailFlyout,
      cancelCloseRailFlyout,
      closeRailFlyout,
      value,
      expandedChildren,
      manuallyExpandedRoutes,
      color,
      activeColor,
      navigationRoutes,
      filteredRoutes,
      routeHasActiveChild,
      routeHasActiveGrandChild,
      isActiveChildRoute,
      filteredChildren,
      toggleChildCollapse,
      toggleManualExpand,
      onRootItemClick,
      rootItemTo,
      isChildExpanded,
      getChildArrowDirection,
      t,
      iconColor,
      textColor,
      arrowDirection,
      adminStore,
      isAdmin,
      isSupport,
      currentRole,
      getRouteName,
      getDisplayName,
    }
  },
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  --va-sidebar-background: var(--va-background-primary);
  background: var(--va-background-primary) !important;
  background-color: var(--va-background-primary) !important;
  border-right: 1px solid color-mix(in srgb, var(--va-background-border) 55%, transparent);
  box-shadow:
    1px 0 0 color-mix(in srgb, #fff 4%, transparent),
    8px 0 24px rgba(15, 23, 42, 0.05);
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.app-sidebar--overlay,
.app-sidebar.app-sidebar--overlay {
  --va-sidebar-background: var(--va-background-primary) !important;
  background: var(--va-background-primary) !important;
  background-color: var(--va-background-primary) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: 8px 0 32px rgba(15, 23, 42, 0.18);
}

.app-sidebar--overlay :deep(.va-sidebar__menu),
.app-sidebar--overlay :deep(.va-sidebar__content),
.app-sidebar--overlay :deep(.va-sidebar__item),
.app-sidebar--overlay :deep(.va-collapse),
.app-sidebar--overlay :deep(.va-collapse__header),
.app-sidebar--overlay :deep(.va-collapse__body),
.app-sidebar--overlay :deep(.va-collapse__content),
.app-sidebar--overlay :deep(.va-collapse__body-wrapper) {
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.app-sidebar__inner {
  height: 100%;
  padding: 0.75rem 0.65rem 1rem;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: color-mix(in srgb, var(--va-background-border) 80%, transparent);
    border-radius: 999px;
  }
}

.app-sidebar__accordion {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.app-sidebar__collapse {
  border: none !important;
  box-shadow: none !important;

  :deep(.va-collapse__header) {
    padding: 0 !important;
    background: transparent !important;
    gap: 0 !important;
    box-shadow: none !important;
    border: none !important;
    border-bottom: none !important;
  }

  /* Hide default Vuestic expand icon (ghost chevron bug) */
  :deep(.va-collapse__expand-icon),
  :deep(.va-collapse__header > .va-icon:not(.app-sidebar__chevron)),
  :deep(.va-collapse__header .va-collapse__expand-icon),
  .app-sidebar__expand-slot {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
    min-width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    opacity: 0 !important;
    overflow: hidden !important;
    pointer-events: none !important;
  }

  /* Remove horizontal dividers between collapse sections */
  :deep(.va-collapse__body-wrapper),
  :deep(.va-collapse__body-wrapper--bordered) {
    border: none !important;
    border-bottom: none !important;
    box-shadow: none !important;
    overflow: hidden !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  :deep(.va-collapse--expanded > .va-collapse__body-wrapper),
  :deep(.va-collapse--expanded .va-collapse__body-wrapper) {
    height: auto !important;
    overflow: visible !important;
    max-height: none !important;
  }

  :deep(.va-collapse__body-wrapper::-webkit-scrollbar) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  :deep(.va-collapse__body) {
    padding: 0 !important;
    background: transparent !important;
    max-height: none !important;
    overflow: visible !important;
    border: none !important;
  }

  :deep(.va-collapse__content) {
    padding: 0 !important;
    background: transparent !important;
    max-height: none !important;
    overflow: visible !important;
    border: none !important;
  }
}

/* Extra safety: kill leftover collapse chrome globally in sidebar */
:deep(.va-collapse) {
  border: none !important;
  box-shadow: none !important;
}

:deep(.va-collapse + .va-collapse) {
  border-top: none !important;
  margin-top: 0 !important;
}

.app-sidebar__item {
  border-radius: 12px !important;
  margin: 0 !important;
  overflow: hidden;
  border: 1px solid transparent;
  background: transparent;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--va-primary) 10%, var(--va-background-element)) !important;
    border-color: color-mix(in srgb, var(--va-primary) 28%, var(--va-background-border));
    box-shadow: none;
  }

  &:active {
    transform: translateY(1px) scale(0.99);
  }

  :deep(.va-sidebar__item__content),
  :deep(.va-sidebar-item-content) {
    background: transparent !important;
    box-shadow: none !important;
  }
}

:deep(.va-sidebar-item),
:deep(.va-sidebar-item--active),
:deep(.va-sidebar-item:hover),
:deep(.va-sidebar-item:focus) {
  box-shadow: none !important;
}

:deep(.app-sidebar__item.va-sidebar-item--active),
:deep(.app-sidebar__item--active),
:deep(.va-sidebar__menu__item--active),
:deep(a.app-sidebar__item.router-link-exact-active) {
  background: color-mix(in srgb, var(--va-primary) 14%, var(--va-background-element)) !important;
  border-color: color-mix(in srgb, var(--va-primary) 35%, var(--va-background-border)) !important;
  box-shadow: none !important;
}

:deep(.va-sidebar__menu),
:deep(.va-sidebar__content) {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.app-sidebar__item-content {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  min-height: 42px;
  padding: 0.55rem 0.75rem !important;
}

.app-sidebar__item-content--child {
  min-height: 38px;
  padding: 0.45rem 0.7rem 0.45rem 1.15rem !important;
}

.app-sidebar__item-content--grand {
  min-height: 36px;
  padding: 0.4rem 0.65rem 0.4rem 1.55rem !important;
}

.app-sidebar__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  font-size: 0.875rem;
  font-weight: 650;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.app-sidebar__title--sm {
  font-size: 0.8125rem;
  font-weight: 600;
}

.app-sidebar__chevron {
  opacity: 0.65;
  transition: transform 0.2s ease;
  flex-shrink: 0;

  &.is-open {
    transform: rotate(180deg);
  }
}

.app-sidebar__children {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0.2rem 0 0.35rem;
  padding: 0.25rem;
  border-radius: 14px;
  background: color-mix(in srgb, var(--va-background-element) 40%, transparent);
  border: 1px solid color-mix(in srgb, var(--va-background-border) 45%, transparent);
  box-shadow: none;
  overflow: visible !important;
  max-height: none !important;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
}

.app-sidebar__item--child,
.app-sidebar__item--grand {
  border-radius: 10px !important;
}

.level-3-children {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin: 0.15rem 0 0.2rem 0.55rem;
  padding-left: 0.45rem;
  border-left: 1px solid color-mix(in srgb, var(--va-primary) 22%, var(--va-background-border));
  overflow: visible !important;
  max-height: none !important;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
}

@media screen and (max-width: 640px) {
  .app-sidebar__inner {
    padding: 0.65rem 0.55rem 1rem;
  }

  .app-sidebar__item-content {
    min-height: 40px;
  }
}

/* Collapsed icon rail (desktop) — layout like mini sidebar, keep project colors */
.app-sidebar--minimized {
  --va-sidebar-item-content-padding: 0;
  --va-sidebar-item-content-gap: 0;
  --va-sidebar-item-active-border-size: 0px;

  .app-sidebar__inner {
    padding: 0.65rem 0.35rem 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .app-sidebar__brand {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 0 0 0.75rem;
    padding: 0.2rem 0 0.55rem;
    text-decoration: none;
    border-bottom: 1px solid color-mix(in srgb, var(--va-background-border) 55%, transparent);
  }

  .app-sidebar__brand-logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }

  .app-sidebar__accordion {
    width: 100%;
    gap: 0.35rem;
    align-items: center;
  }

  .app-sidebar__collapse {
    width: 100%;
  }

  .app-sidebar__title,
  .app-sidebar__chevron,
  .app-sidebar__children,
  .level-3-children,
  :deep(.va-sidebar__title),
  :deep(.va-sidebar-item-title) {
    display: none !important;
    width: 0 !important;
    min-width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
  }

  .app-sidebar__item {
    display: flex !important;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-radius: 10px !important;
    padding-right: 0 !important;
    border-left: none !important;
  }

  .app-sidebar__item-content,
  :deep(.va-sidebar__item__content),
  :deep(.va-sidebar-item-content) {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 0 !important;
    width: 100% !important;
    min-height: 42px;
    padding: 0.55rem 0 !important;
  }

  .app-sidebar__item-content--child,
  .app-sidebar__item-content--grand {
    padding: 0.55rem 0 !important;
  }

  :deep(.va-sidebar__item__content > *),
  :deep(.va-sidebar-item-content > *),
  .app-sidebar__item-content > * {
    margin: 0 !important;
  }

  :deep(.va-icon) {
    display: flex !important;
    align-items: center;
    justify-content: center;
    margin: 0 auto !important;
  }

  :deep(.va-collapse__header) {
    display: flex !important;
    justify-content: center;
    width: 100%;
  }

  :deep(.va-collapse__body-wrapper),
  :deep(.va-collapse__body),
  :deep(.va-collapse__content) {
    display: none !important;
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .app-sidebar__rail-anchor {
    width: 100%;
  }
}
</style>

<!-- Teleported flyout lives on body — keep styles global for this panel only -->
<style lang="scss">
.app-sidebar-flyout {
  position: fixed;
  z-index: 10050;
  min-width: 200px;
  max-width: min(280px, calc(100vw - 5.5rem));
  max-height: min(70vh, 420px);
  overflow-y: auto;
  padding: 8px;
  border-radius: 12px;
  background: var(--va-background-primary);
  border: 1px solid color-mix(in srgb, var(--va-background-border) 70%, transparent);
  box-shadow:
    0 10px 30px rgba(15, 23, 42, 0.18),
    0 0 0 1px color-mix(in srgb, #fff 4%, transparent);
}

.app-sidebar-flyout__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid color-mix(in srgb, var(--va-background-border) 55%, transparent);
}

.app-sidebar-flyout__header--link {
  text-decoration: none;
  border-radius: 8px;
  transition: background-color 0.12s ease;
}

.app-sidebar-flyout__header--link:hover {
  background: color-mix(in srgb, var(--va-primary) 12%, var(--va-background-element));
}

.app-sidebar-flyout__header--link:only-child {
  margin-bottom: 0;
  border-bottom: none;
}

.app-sidebar-flyout__title {
  font-size: 13px;
  font-weight: 700;
  color: var(--va-text-primary);
  line-height: 1.25;
}

.app-sidebar-flyout__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 4px;
}

.app-sidebar-flyout__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.app-sidebar-flyout__group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--va-secondary);
}

.app-sidebar-flyout__link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--va-text-primary);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.app-sidebar-flyout__link:hover {
  background: color-mix(in srgb, var(--va-primary) 12%, var(--va-background-element));
  color: var(--va-primary);
}

.app-sidebar-flyout__link.is-active {
  background: color-mix(in srgb, var(--va-primary) 16%, var(--va-background-element));
  color: var(--va-primary);
  font-weight: 650;
}

.app-sidebar-flyout__group .app-sidebar-flyout__link {
  padding-left: 18px;
}
</style>
